<template>
    <v-container>
        <div class="mb-3 text-center">Note: This portal searches on standards titles and summary descriptions, not within the full text of the standard itself.</div>
        <v-card outlined class="mb-5">
            <v-card-title class="clickable pa-1" @click="$store.set('filters@show', !show)">
                <v-row no-gutters align="center">
                    <v-col cols="auto">
                        <v-fab-transition hide-on-leave>
                            <v-btn icon aria-label="Show filters" v-if="!show">
                                <v-icon color="primary">mdi-plus</v-icon>
                            </v-btn>
                        </v-fab-transition>
                        <v-fab-transition hide-on-leave>
                            <v-btn icon aria-label="Hide filters" v-if="show">
                                <v-icon color="primary">mdi-minus</v-icon>
                            </v-btn>
                        </v-fab-transition>
                        <span class="title primary--text mr-3">Search Filters</span>
                    </v-col>
                    <v-col>
                        <span>
                            <v-chip small label close @click:close="close('text')" class="mr-1" color="primary" v-if="text && text.length">{{ text }}</v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('fda')" class="mr-1" color="primary" v-if="fda">FDA recognized</v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('bundles')" class="mr-1" color="primary" v-if="bundles">ANSI packages</v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('feature')" class="mr-1" color="primary" v-if="feature">Featured: {{ featuredText }}</v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('selectedSectors', index)" class="mr-1" color="primary" v-for="(selectedSector, index) in selectedSectors" :key="index">
                                {{ selectedSector }}
                            </v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('selectedFunctionalAreas', index)" class="mr-1" color="primary" v-for="(selectedFunctionalArea, index) in selectedFunctionalAreas" :key="index">
                                {{ selectedFunctionalArea }}
                            </v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('selectedSdos', index)" class="mr-1" color="primary" v-for="(selectedSdo, index) in selectedSdos" :key="index">
                                {{ selectedSdo }}
                            </v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('selectedStatuses', index)" class="mr-1" color="primary" v-for="(selectedStatus, index) in selectedStatuses" :key="index">
                                {{ statusDisplay(selectedStatus) }}
                            </v-chip>
                        </span>
                        <span>
                            <v-chip small label close @click:close="close('selectedTypes', index)" class="mr-1" color="primary" v-for="(selectedType, index) in selectedTypes" :key="index">
                                {{ selectedType }}
                            </v-chip>
                        </span>
                    </v-col>
                    <v-col cols="auto" class="text-right">
                        <span class="subtitle-1 ml-3" v-if="hasActiveFilters">
                            <a @click.stop="resetAll()">Reset all filters</a>
                        </span>
                        <v-progress-circular size="20" width="2" indeterminate color="primary" :class="{ hidden: !loading }" class="mx-3"></v-progress-circular>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-slide-y-transition leave-absolute>
                <div v-if="show">
                    <v-divider></v-divider>
                    <v-row no-gutters class="ma-3 mb-n3">
                        <v-col class="text-right">
                            <a class="primary--text body-2 ml-3" @click="$root.$definitionsDialog.show()">
                                <span>Help with definitions</span>
                                <v-icon size="20" color="primary" class="ml-1">mdi-help-circle-outline</v-icon>
                            </a>
                        </v-col>
                    </v-row>
                    <v-row no-gutters align="start" class="ma-3 mb-n2">
                        <v-col cols="4">
                            <v-text-field
                                v-model="text"
                                @input="searchDebounced()"
                                prepend-inner-icon="mdi-magnify"
                                label="Enter keywords"
                                clearable
                                outlined
                                dense
                                persistent-hint
                                hint='Use double quotes to search for an exact match ("F2233-22")'
                            ></v-text-field>
                        </v-col>
                        <v-col cols="8" class="pt-1 pl-6">
                            <span class="title text-uppercase primary--text">Special Designations:</span>
                            <v-btn text @click="fda = !fda; searchDebounced()" class="mt-n1">
                                <v-icon left v-if="fda">mdi-checkbox-marked-outline</v-icon>
                                <v-icon left v-else>mdi-checkbox-blank-outline</v-icon>
                                FDA recognized
                            </v-btn>
                            <v-btn text @click="bundles = !bundles; searchDebounced()" class="mt-n1">
                                <v-icon left v-if="bundles">mdi-checkbox-marked-outline</v-icon>
                                <v-icon left v-else>mdi-checkbox-blank-outline</v-icon>
                                ANSI packages
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-card-text class="mt-6 py-0">
                        <v-row>
                            <v-col cols="2" class="pb-0">
                                <div class="title text-uppercase primary--text">Sector</div>
                                <v-select dense flat v-model="selectedSectors" :items="sectors" @change="searchDebounced()" multiple clearable outlined single-line label="All">
                                    <template v-slot:selection="{ item, index }">
                                        <span class="text-truncate" v-if="1 === selectedSectors.length">{{ item }}</span>
                                        <span class="text-truncate" v-else-if="0 === index">{{ selectedSectors.length }} selected</span>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item @click="all('selectedSectors')" :class="{ 'primary--text v-list-item--active': !selectedSectors.length }">
                                            <v-list-item-action>
                                                <v-icon :color="selectedSectors.length ? '' : 'primary'">{{ selectedSectors.length ? 'mdi-radiobox-blank' : 'mdi-radiobox-marked' }}</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>All</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mb-2"></v-divider>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="3" class="pb-0">
                                <div class="title text-uppercase text-truncate primary--text">Functional Area</div>
                                <v-select dense flat v-model="selectedFunctionalAreas" :items="functionalAreas" @change="searchDebounced()" multiple clearable outlined single-line label="All">
                                    <template v-slot:selection="{ item, index }">
                                        <span class="text-truncate" v-if="1 === selectedFunctionalAreas.length">{{ item }}</span>
                                        <span class="text-truncate" v-else-if="0 === index">{{ selectedFunctionalAreas.length }} selected</span>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item @click="all('selectedFunctionalAreas')" :class="{ 'primary--text v-list-item--active': !selectedFunctionalAreas.length }">
                                            <v-list-item-action>
                                                <v-icon :color="selectedFunctionalAreas.length ? '' : 'primary'">{{ selectedFunctionalAreas.length ? 'mdi-radiobox-blank' : 'mdi-radiobox-marked' }}</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>All</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mb-2"></v-divider>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="3" class="pb-0">
                                <div class="title text-uppercase primary--text">Organization</div>
                                <v-select dense flat v-model="selectedSdos" :items="sdos" @change="searchDebounced()" multiple clearable outlined single-line label="All">
                                    <template v-slot:selection="{ item, index }">
                                        <span class="text-truncate" v-if="1 === selectedSdos.length">{{ item }}</span>
                                        <span class="text-truncate" v-else-if="0 === index">{{ selectedSdos.length }} selected</span>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item @click="all('selectedSdos')" :class="{ 'primary--text v-list-item--active': !selectedSdos.length }">
                                            <v-list-item-action>
                                                <v-icon :color="selectedSdos.length ? '' : 'primary'">{{ selectedSdos.length ? 'mdi-radiobox-blank' : 'mdi-radiobox-marked' }}</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>All</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mb-2"></v-divider>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="2" class="pb-0">
                                <div class="title text-uppercase primary--text">Status</div>
                                <v-select dense flat v-model="selectedStatuses" :items="statuses" @change="searchDebounced()" multiple clearable outlined single-line label="All">
                                    <template v-slot:item="{ item }">
                                        <v-icon class="mr-6" :color="selectedStatuses.includes(item) ? 'primary' : ''">{{ selectedStatuses.includes(item) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
                                        <span class="pl-2 v-list-item__subtitle">{{ statusDisplay(item) }}</span>
                                    </template>
                                    <template v-slot:selection="{ item, index }">
                                        <span class="text-truncate" v-if="1 === selectedStatuses.length">{{ statusDisplay(item) }}</span>
                                        <span class="text-truncate" v-else-if="0 === index">{{ selectedStatuses.length }} selected</span>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item @click="all('selectedStatuses')" :class="{ 'primary--text v-list-item--active': !selectedStatuses.length }">
                                            <v-list-item-action>
                                                <v-icon :color="selectedStatuses.length ? '' : 'primary'">{{ selectedStatuses.length ? 'mdi-radiobox-blank' : 'mdi-radiobox-marked' }}</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>All</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mb-2"></v-divider>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="2" class="pb-0">
                                <div class="title text-uppercase primary--text">Type</div>
                                <v-select dense flat v-model="selectedTypes" :items="types" @change="searchDebounced()" multiple clearable outlined single-line label="All">
                                    <template v-slot:selection="{ item, index }">
                                        <span class="text-truncate" v-if="1 === selectedTypes.length">{{ item }}</span>
                                        <span class="text-truncate" v-else-if="0 === index">{{ selectedTypes.length }} selected</span>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item @click="all('selectedTypes')" :class="{ 'primary--text v-list-item--active': !selectedTypes.length }">
                                            <v-list-item-action>
                                                <v-icon :color="selectedTypes.length ? '' : 'primary'">{{ selectedTypes.length ? 'mdi-radiobox-blank' : 'mdi-radiobox-marked' }}</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>All</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mb-2"></v-divider>
                                    </template>
                                </v-select>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </div>
            </v-slide-y-transition>
        </v-card>
        <div>
            <v-card flat tile>
                <v-toolbar dense flat dark color="primary">
                    <v-toolbar-title class="title text-uppercase font-weight-bold">Search Results</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn text @click="fda = !fda; searchDebounced()">
                        <v-icon left v-if="fda">mdi-checkbox-marked-outline</v-icon>
                        <v-icon left v-else>mdi-checkbox-blank-outline</v-icon>
                        Show FDA recognized
                    </v-btn>
                    <v-btn text @click="bundles = !bundles; searchDebounced()">
                        <v-icon left v-if="bundles">mdi-checkbox-marked-outline</v-icon>
                        <v-icon left v-else>mdi-checkbox-blank-outline</v-icon>
                        Show ANSI packages
                    </v-btn>
                    <v-divider vertical inset class="mx-3 white"></v-divider>
                    <ClipboardCopy :text="shareLink" tooltip="Get a unique share link for this search" copiedTooltip="Link copied to clipboard!">
                        <template #default="{ on, attrs, click }">
                            <v-btn text @click="click()" v-bind="attrs" v-on="on">
                                <v-icon left>mdi-export-variant</v-icon>
                                Share Results
                            </v-btn>
                        </template>
                    </ClipboardCopy>
                </v-toolbar>
                <v-row no-gutters>
                    <v-col>
                        <SearchResultsCount :icon="icon('Standards')" :value="countStandards" label="Standards"></SearchResultsCount>
                    </v-col>
                    <v-col>
                        <SearchResultsCount :icon="icon('Organizations')" :value="countOrganizations" label="Organizations"></SearchResultsCount>
                    </v-col>
                    <v-col>
                        <SearchResultsCount :icon="icon('Available')" :value="countAvailable" :label="statusDisplay('Available')"></SearchResultsCount>
                    </v-col>
                    <v-col>
                        <SearchResultsCount :icon="icon('In Development')" :value="countInDevelopment" label="In Development"></SearchResultsCount>
                    </v-col>
                    <v-col>
                        <SearchResultsCount :icon="icon('Area of Need')" :value="countAreasOfNeed" label="Areas of Need"></SearchResultsCount>
                    </v-col>
                </v-row>
            </v-card>
            <SearchResultsTable :standards="standards"></SearchResultsTable>
        </div>
    </v-container>
</template>

<script>
    import { call, get, sync } from 'vuex-pathify';
    import { debounce } from 'lodash';
    import ClipboardCopy from '@/components/ClipboardCopy';
    import SearchResultsCount from '@/components/SearchResultsCount';
    import SearchResultsTable from '@/components/SearchResultsTable';
    import { icon, statusDisplay, features } from '@/common';

    export default {
        name: 'Search',
        components: { ClipboardCopy, SearchResultsTable, SearchResultsCount },
        computed: {
            featuredText() {
                return this.feature ? features.find((f) => f.value === this.feature)?.text : '';
            },
            shareLink: get('shareLink'),
            encodedParams: get('encodedParams'),
            ...get('common@*'),
            ...get('count*'),
            hasActiveFilters: get('hasActiveFilters'),
            loading: get('loading'),
            standards: get('standards'),
            sdos: get('sdos'),
            ...sync('filters@*'),
            ...get('selected*')
        },
        beforeRouteEnter(to, from, next) {
            next((vm) => {
                const permalink = to.params?.permalink;
                if (permalink && permalink !== vm.encodedParams) {
                    vm.$store.dispatch('permalink', permalink);
                }
            });
        },
        watch: {
            encodedParams: {
                immediate: true,
                handler(value) {
                    if (value !== this.$router.currentRoute?.params?.permalink) {
                        this.$router.replace({ params: { permalink: value } });
                    }
                }
            }
        },
        methods: {
            icon: icon,
            statusDisplay: statusDisplay,
            close(prop, index) {
                if ('text' === prop) {
                    this.$store.set('filters@text', '');
                } else if ('fda' === prop) {
                    this.$store.set('filters@fda', false);
                } else if ('bundles' === prop) {
                    this.$store.set('filters@bundles', false);
                } else if ('feature' === prop) {
                    this.$store.set('filters@feature', '');
                } else {
                    const arr = [...this[prop]];
                    arr.splice(index, 1);
                    this.$store.set(`filters@${prop}`, arr);
                }
                this.searchDebounced();
            },
            all(prop) {
                this.$store.set(`filters@${prop}`, []);
                this.searchDebounced();
            },
            resetAll() {
                this.$store.commit('resetFilters');
                this.searchDebounced();
            },
            search: call('search'),
            searchDebounced: debounce(function () {
                this.search();
            }, 500)
        }
    };
</script>

<style lang="scss" scoped>
    ::v-deep .v-list--dense .v-list-item {
        min-height: 0;
    }
    ::v-deep .v-list-item {
        opacity: 0.75;
    }
    ::v-deep .v-list-item--active {
        opacity: 1;
    }

    .text-truncate {
        max-width: 90%;
    }

    .clickable {
        cursor: pointer;
    }
    .hidden {
        visibility: hidden;
    }
</style>
